/** BookingSyncForm — the dashboard's booking sync (booking sync plan WP-D), a port of the console's
 *  (console/src/features/sync/BookingSyncForm.tsx): either filter by criteria (tours / external IDs + booking-start
 *  range + updated / created range, as the booking system's `booking_query` allows) or give booking IDs (chips +
 *  a .txt upload), set the advanced options, then start the background task (`POST bg_tasks/start_sync/`).
 *
 *  Hosted by Settings › Sync Bookings (its own scope pickers: booking system, account) and by the Booking-system
 *  sync page's Bookings card (`scope` locked to the page's booking system and account). In both a franchise parent
 *  picks the licensee first: every read names it (`franchise_to`), the start too (`tour_operator`). Dashboard-only
 *  parts: the Licensee picker, the sandbox note, the single booking system chosen by itself. Notifications and
 *  surveys start off: they go to real guests. */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress,
    FormControlLabel, MenuItem, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment, { Moment } from 'moment';
import * as React from 'react';

import LicenseeFormField from '../../components/LicenseeFormField';
import SandboxSyncNote from '../../developer/SandboxSyncNote';
import { ContextStore } from '../../store/contextStore';
import { errorCode, SyncApiError } from '../api';
import { BOOKING_SYNC_PERMISSION, errorText } from '../labels';
import type { SyncTask } from '../types';
import { useBookingSyncApi } from './api';
import type { BookingSyncAccount, BookingSyncIntegration, ImportSource, StartSyncBody, SyncBookingsTarget } from './types';

/** One selectable option = one external ID belonging to a tour. */
interface TargetOption {
    /** Tour + kind + value: an ID two tours share is a separate option in each (a tour offers an ID once). */
    id: string;
    tourId: number;
    tourName: string;
    value: string;
    kind: 'primary' | 'ext';
    idType?: string;
}

const QUICK_RANGES: { label: string; compute: (from: Moment) => Moment }[] = [
    { label: '1 day', compute: (d) => d.clone().add(1, 'day') },
    { label: '3 days', compute: (d) => d.clone().add(3, 'days') },
    { label: '1 week', compute: (d) => d.clone().add(7, 'days') },
    { label: '2 weeks', compute: (d) => d.clone().add(14, 'days') },
    { label: '1 month', compute: (d) => d.clone().add(1, 'month') },
    { label: '3 months', compute: (d) => d.clone().add(3, 'months') },
    { label: '6 months', compute: (d) => d.clone().add(6, 'months') },
    { label: '1 year', compute: (d) => d.clone().add(1, 'year') },
];

/** Presets for the updated/created range ("updated in the last N hours"). */
const UPDATED_PRESETS: { label: string; hours: number }[] = [
    { label: 'Last hour', hours: 1 },
    { label: 'Last 3h', hours: 3 },
    { label: 'Last 24h', hours: 24 },
    { label: 'Last 7 days', hours: 168 },
];

/** Above this many booking IDs the chip field collapses into a summary. */
const IDS_COLLAPSE_THRESHOLD = 100;

const isMidnight = (m: Moment) => m.hours() === 0 && m.minutes() === 0 && m.seconds() === 0;

const groupOf = (o: TargetOption) => `${o.tourName} [#${o.tourId}]`;
const optionLabel = (o: TargetOption) => o.value;
const sameOption = (a: TargetOption, b: TargetOption) => a.id === b.id;

/** A refusal in words; without `ops.sync_bookings` it names the permission. */
const refusalText = (e: unknown) => (errorCode(e) === 'bgtask_permission_required' ? BOOKING_SYNC_PERMISSION : errorText(e));

/** The chosen booking system is not one this tenant (and account) may start a booking sync of here. */
const notOffered = () => new SyncApiError(0, {
    detail: "This booking system's bookings can't be synced from here. Reload the page, or ask our team.",
});

/** Feeds `promise`'s answer to `onData` (its refusal to `onError`) unless `controller` was aborted meanwhile: a
 *  newer scope asked again, or the form was left. */
function settle<T>(controller: AbortController, promise: Promise<T>, onData: (data: T) => void, onError: (e: unknown) => void) {
    promise.then(
        (data) => !controller.signal.aborted && onData(data),
        (e) => !controller.signal.aborted && onError(e),
    );
}

/** A From / To picker of the granularity the booking system queries by. */
function RangePicker({ granularity, label, value, onChange, width, helperText }: {
    granularity: 'date' | 'datetime';
    label: string;
    value: Moment | null;
    onChange: (value: Moment | null) => void;
    width: number;
    helperText?: string;
}) {
    const slotProps = { textField: { size: 'small' as const, sx: { width }, helperText } };
    return granularity === 'datetime'
        ? <DateTimePicker label={label} value={value} onChange={onChange} slotProps={slotProps} />
        : <DatePicker label={label} value={value} onChange={onChange} slotProps={slotProps} />;
}

/** The booking system and account the form syncs for, when its host owns them (the sync page's card). */
export interface BookingSyncScope {
    integration: string;
    /** The account; null for the default account. */
    account: string | null;
}

export interface BookingSyncFormProps {
    /** Locked to the host's booking system and account; without it the form offers its own pickers (booking
     *  system, account). A franchise parent picks the licensee either way. */
    scope?: BookingSyncScope | null;
    /** The started run (`start_sync`'s answer). */
    onLaunched?: (taskId: string, task: SyncTask) => void;
}

export default function BookingSyncForm({ scope = null, onLaunched }: BookingSyncFormProps) {
    const api = useBookingSyncApi();
    const { isFranchise, currentLicensee, environment } = React.useContext(ContextStore) as any;
    const locked = scope != null;

    // The scope: the host's, or the form's own pickers; a franchise parent picks the licensee first in both (its
    // licensees' bookings are synced, never the parent's own: `start_sync` refuses a parent without `tour_operator`).
    const [licensee, setLicensee] = React.useState<number | null>(currentLicensee || null);
    const [integrations, setIntegrations] = React.useState<BookingSyncIntegration[] | null>(null);
    const [pickedIntegration, setPickedIntegration] = React.useState('');
    const [accounts, setAccounts] = React.useState<BookingSyncAccount[]>([]);
    const [pickedAccount, setPickedAccount] = React.useState<string | null>(null);
    const franchiseTo: number | null = isFranchise ? licensee : null;
    const tenantReady = !isFranchise || licensee != null;
    const integration = locked ? scope.integration : pickedIntegration;
    const account = locked ? scope.account : pickedAccount;

    const [detail, setDetail] = React.useState<BookingSyncIntegration | null>(null);
    const [importSources, setImportSources] = React.useState<ImportSource[]>([]);
    const [targets, setTargets] = React.useState<SyncBookingsTarget[] | null>(null);
    const [targetsLoading, setTargetsLoading] = React.useState(false);
    const [loadError, setLoadError] = React.useState<unknown>(null);

    const [mode, setMode] = React.useState<'criteria' | 'ids'>('criteria');
    const [selected, setSelected] = React.useState<TargetOption[]>([]);
    const tomorrow = React.useMemo(() => moment().startOf('day').add(1, 'day'), []);
    const [fromDate, setFromDate] = React.useState<Moment | null>(tomorrow);
    const [toDate, setToDate] = React.useState<Moment | null>(() => tomorrow.clone().add(1, 'year'));
    const [updatedFrom, setUpdatedFrom] = React.useState<Moment | null>(null);
    const [updatedTo, setUpdatedTo] = React.useState<Moment | null>(null);
    /** Which range drives the query when the integration can't combine both. */
    const [rangeMode, setRangeMode] = React.useState<'start' | 'updated'>('start');
    const [bookingIds, setBookingIds] = React.useState<string[]>([]);
    const [fileNote, setFileNote] = React.useState<{ severity: 'success' | 'warning' | 'error'; text: string } | null>(null);
    const [dryRun, setDryRun] = React.useState(false);
    // Advanced options
    const [source, setSource] = React.useState('');
    const [sendNotifications, setSendNotifications] = React.useState(false);
    const [sendSurveys, setSendSurveys] = React.useState(false);
    const [statuses, setStatuses] = React.useState('');
    const [reconcile, setReconcile] = React.useState(false);
    const [approval, setApproval] = React.useState(false);
    const [batchMultiplier, setBatchMultiplier] = React.useState(1);
    const [extraArgs, setExtraArgs] = React.useState<Record<string, string>>({});

    const [starting, setStarting] = React.useState(false);
    const [startError, setStartError] = React.useState<unknown>(null);
    const alive = React.useRef(true);
    React.useEffect(() => () => {
        alive.current = false;
    }, []);
    const bookingIdsRef = React.useRef(bookingIds);
    bookingIdsRef.current = bookingIds;

    // A different licensee / integration / account invalidates the tour selection, the updated range, the range
    // mode and the staged extra args (the console's resets), and the booking IDs with their mode (the old Sync
    // Bookings page's: another system's IDs mean nothing here). Declared before the reads: it runs first on a
    // change, so it never clears what a read of the new scope reports.
    const scopeKey = `${franchiseTo ?? ''}|${integration}|${account ?? ''}`;
    React.useEffect(() => {
        setSelected([]);
        setUpdatedFrom(null);
        setUpdatedTo(null);
        setRangeMode('start');
        setExtraArgs({});
        setMode('criteria');
        setBookingIds([]);
        setFileNote(null);
        setLoadError(null);
        setStartError(null);
    }, [scopeKey]);

    // Own pickers: the booking systems whose sync can be started here (`ui_launchable: false` ones are run by
    // ops); a single one is chosen by itself.
    React.useEffect(() => {
        setIntegrations(null);
        setPickedIntegration('');
        if (locked || !tenantReady) {
            return undefined;
        }
        const controller = new AbortController();
        settle(controller, api.integrations(franchiseTo, null, controller.signal), (rows) => {
            const list = (rows || []).filter((r) => r.booking_query?.ui_launchable !== false);
            setIntegrations(list);
            setPickedIntegration(list.length === 1 ? list[0].name : '');
        }, setLoadError);
        return () => controller.abort();
    }, [api, locked, tenantReady, franchiseTo]);

    React.useEffect(() => {
        setAccounts([]);
        setPickedAccount(null);
        if (locked || !tenantReady || !pickedIntegration) {
            return undefined;
        }
        const controller = new AbortController();
        settle(controller, api.accounts(pickedIntegration, franchiseTo, controller.signal), (rows) => setAccounts(rows || []), setLoadError);
        return () => controller.abort();
    }, [api, locked, tenantReady, franchiseTo, pickedIntegration]);

    // The chosen booking system as configured for the chosen account: what it can query, the arguments it needs
    // (an account can change those, e.g. FareHarbor's company). The default account's is the list's row.
    React.useEffect(() => {
        setDetail(null);
        if (!tenantReady || !integration) {
            return undefined;
        }
        const take = (rows: BookingSyncIntegration[] | null) => {
            const row = (rows || []).find((i) => i.name === integration);
            if (row) {
                setDetail(row);
            } else {
                setLoadError(notOffered());
            }
        };
        if (!locked && !account) {
            take(integrations);
            return undefined;
        }
        const controller = new AbortController();
        settle(controller, api.integrations(franchiseTo, account, controller.signal), take, setLoadError);
        return () => controller.abort();
    }, [api, locked, tenantReady, franchiseTo, integration, account, integrations]);

    React.useEffect(() => {
        setImportSources([]);
        setSource('');
        if (!tenantReady) {
            return undefined;
        }
        const controller = new AbortController();
        settle(controller, api.importSources(franchiseTo, controller.signal), (rows) => setImportSources(rows || []), setLoadError);
        return () => controller.abort();
    }, [api, tenantReady, franchiseTo]);

    React.useEffect(() => {
        setTargets(null);
        const ready = tenantReady && Boolean(integration);
        setTargetsLoading(ready);
        if (!ready) {
            return undefined;
        }
        const controller = new AbortController();
        settle(controller, api.targets(integration, account, franchiseTo, controller.signal), (answer) => {
            setTargets(answer?.results || []);
            setTargetsLoading(false);
        }, (e) => {
            setLoadError(e);
            setTargetsLoading(false);
        });
        return () => controller.abort();
    }, [api, tenantReady, franchiseTo, integration, account]);

    const scopeReady = tenantReady && Boolean(integration);
    // Nothing is started, and no capability-dependent field is offered, before the booking system's capabilities
    // and arguments are read (else a FareHarbor sync could start without its company, or with dates for a
    // datetime range); a failed read keeps it so.
    const capsLoaded = detail != null;
    const capsReady = scopeReady && capsLoaded && loadError == null;

    // Capabilities of the selected integration (SYNC_BOOKINGS_QUERY).
    const bq = detail?.booking_query ?? null;
    const startGran = bq?.start_range ?? 'date';
    const updGran = bq?.updated_range ?? null;
    const updatedLabel = bq?.updated_semantics === 'created' ? 'Created' : 'Updated';
    const canCombine = bq?.combined ?? false;
    const byIds = bq?.by_ids ?? true;

    const effMode = byIds ? mode : 'criteria';
    const startActive = capsLoaded && effMode === 'criteria' && (!updGran || canCombine || rangeMode === 'start');
    const updatedActive = capsLoaded && effMode === 'criteria' && updGran !== null && (canCombine || rangeMode === 'updated');

    const argDefs = React.useMemo(() => detail?.extra_args ?? [], [detail]);
    const missingRequiredArgs = argDefs.filter((a) => a.required && !(extraArgs[a.name] ?? '').trim());

    const options = React.useMemo<TargetOption[]>(() => {
        const out: TargetOption[] = [];
        (targets || []).forEach((t) => {
            // Within a tour an ID is offered once: its own external ID may also be one of its extra ones.
            const seen = new Set<string>();
            const add = (value: string, kind: 'primary' | 'ext', idType?: string) => {
                if (seen.has(value)) {
                    return;
                }
                seen.add(value);
                out.push({ id: `${t.tour.id}|${kind}|${value}`, tourId: t.tour.id, tourName: t.tour.name, value, kind, idType });
            };
            if (t.primary) {
                add(t.primary, 'primary');
            }
            t.ext_ids.forEach((e) => add(e.value, 'ext', e.id_type));
        });
        return out;
    }, [targets]);
    const pickedIds = React.useMemo(() => new Set(selected.map((o) => o.id)), [selected]);

    /** Booking-start bound → wire format. Date-granularity integrations get plain dates; datetime-granularity get
     *  UTC ISO, with an untouched (00:00) "to" time expanded to end-of-day per the range convention. */
    const fmtStart = (m: Moment, isEnd: boolean): string => {
        if (startGran === 'date') {
            return m.format('YYYY-MM-DD');
        }
        const eff = isEnd && isMidnight(m) ? m.clone().endOf('day') : m;
        return eff.toISOString();
    };
    const fmtUpdated = (m: Moment): string => (updGran === 'date' ? m.format('YYYY-MM-DD') : m.toISOString());

    const start = async () => {
        const cleanArgs: Record<string, string> = {};
        argDefs.forEach((a) => {
            const v = (extraArgs[a.name] ?? '').trim();
            if (v) {
                cleanArgs[a.name] = v;
            }
        });
        const statusList = statuses.split(/[,\n]+/).map((s) => s.trim()).filter(Boolean);
        const body: StartSyncBody = {
            integration_name: integration,
            account,
            ...(franchiseTo != null ? { tour_operator: franchiseTo } : {}),
            ...(effMode === 'ids'
                ? { booking_ids: bookingIds }
                : {
                    ...(startActive && fromDate && toDate
                        ? { from_date: fmtStart(fromDate, false), to_date: fmtStart(toDate, true) }
                        : {}),
                    ...(updatedActive && updatedFrom
                        ? { updated_from: fmtUpdated(updatedFrom), ...(updatedTo ? { updated_to: fmtUpdated(updatedTo) } : {}) }
                        : {}),
                    ...(selected.length ? { product_ids: Array.from(new Set(selected.map((o) => o.value))) } : {}),
                }),
            send_notifications: sendNotifications,
            send_surveys: sendSurveys,
            reconcile_existing: reconcile,
            dry_run: dryRun,
            approve_before_import: !dryRun && approval,
            ...(statusList.length ? { statuses: statusList } : {}),
            ...(source ? { source_id: source } : {}),
            ...(batchMultiplier !== 1 ? { batch_multiplier: batchMultiplier } : {}),
            ...(Object.keys(cleanArgs).length ? { extra_args: cleanArgs } : {}),
        };
        setStarting(true);
        setStartError(null);
        try {
            const task = await api.start(body);
            if (alive.current) {
                onLaunched?.(task.task_id, task);
            }
        } catch (e) {
            if (alive.current) {
                setStartError(e);
            }
        } finally {
            if (alive.current) {
                setStarting(false);
            }
        }
    };

    /** Group toggle: select/deselect every ID of a tour. */
    const toggleTour = (tourId: number) => {
        const tourOptions = options.filter((o) => o.tourId === tourId);
        const allIn = tourOptions.every((o) => pickedIds.has(o.id));
        setSelected((prev) => (allIn
            ? prev.filter((s) => s.tourId !== tourId)
            : [...prev.filter((s) => s.tourId !== tourId), ...tourOptions]));
    };

    /** Merge a text file (one booking ID per line) into the chips, deduped. */
    const handleIdsFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-uploading the same file
        if (!file) {
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const lines = String(reader.result ?? '').split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
            if (!lines.length) {
                setFileNote({ severity: 'warning', text: 'The file contains no booking IDs.' });
                return;
            }
            const prev = bookingIdsRef.current;
            const merged = Array.from(new Set([...prev, ...lines]));
            setBookingIds(merged);
            setFileNote({ severity: 'success', text: `Loaded ${merged.length - prev.length} new booking ID(s) from ${file.name}.` });
        };
        reader.onerror = () => setFileNote({ severity: 'error', text: `Could not read ${file.name}.` });
        reader.readAsText(file);
    };

    const rangesOk = effMode === 'ids'
        ? bookingIds.length > 0
        : Boolean((startActive && fromDate && toDate) || (updatedActive && updatedFrom));

    const integrationLabel = detail?.label || integration;

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box>
                {environment === 'sandbox' && <SandboxSyncNote sx={{ mt: 0, mb: 2 }} />}

                {isFranchise && (
                    <Box sx={{ mb: 2, maxWidth: 420 }}>
                        <LicenseeFormField
                            value={licensee}
                            onChange={setLicensee}
                            error={!licensee}
                            helperText={!licensee ? 'Please select a licensee' : undefined}
                        />
                    </Box>
                )}

                {!locked && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
                        <TextField
                            select
                            size="small"
                            label="Integration"
                            value={pickedIntegration}
                            onChange={(e) => setPickedIntegration(e.target.value)}
                            disabled={!integrations || integrations.length <= 1}
                            helperText={integrations && integrations.length === 0 ? 'No connected booking system offers a booking sync' : undefined}
                            inputProps={{ 'data-testid': 'integration-select' }}
                            sx={{ minWidth: 220 }}
                        >
                            {(integrations || []).map((i) => <MenuItem key={i.name} value={i.name}>{i.label}</MenuItem>)}
                        </TextField>
                        {accounts.length > 1 && (
                            <TextField
                                select
                                size="small"
                                label="Account"
                                value={pickedAccount ?? ''}
                                onChange={(e) => setPickedAccount(e.target.value || null)}
                                inputProps={{ 'data-testid': 'account-select' }}
                                sx={{ minWidth: 180 }}
                            >
                                {accounts.map((a) => <MenuItem key={a.slug ?? ''} value={a.slug ?? ''}>{a.label}</MenuItem>)}
                            </TextField>
                        )}
                        {targetsLoading && <CircularProgress size={20} aria-label="Loading the tours" />}
                    </Box>
                )}

                {loadError != null && (
                    <Alert severity="warning" sx={{ mb: 2, maxWidth: 900 }} data-testid="booking-sync-load-error">{refusalText(loadError)}</Alert>
                )}

                {argDefs.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                        {argDefs.map((a) => (a.type === 'select' ? (
                            <TextField
                                key={a.name}
                                select
                                size="small"
                                required={a.required}
                                label={a.label}
                                value={extraArgs[a.name] ?? ''}
                                onChange={(e) => setExtraArgs((p) => ({ ...p, [a.name]: e.target.value }))}
                                inputProps={{ 'data-testid': `extra-arg-${a.name}` }}
                                sx={{ minWidth: 220 }}
                            >
                                {!a.required && <MenuItem value="">- None -</MenuItem>}
                                {(a.choices ?? []).map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                            </TextField>
                        ) : (
                            <TextField
                                key={a.name}
                                size="small"
                                required={a.required}
                                label={a.label}
                                value={extraArgs[a.name] ?? ''}
                                onChange={(e) => setExtraArgs((p) => ({ ...p, [a.name]: e.target.value }))}
                                inputProps={{ 'data-testid': `extra-arg-${a.name}` }}
                                sx={{ minWidth: 220 }}
                            />
                        )))}
                    </Box>
                )}

                <Stack spacing={2} sx={{ maxWidth: 900 }}>
                    {capsLoaded && byIds && (
                        <Box>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={effMode}
                                onChange={(_e, v) => v && setMode(v as 'criteria' | 'ids')}
                            >
                                <ToggleButton value="criteria">By criteria</ToggleButton>
                                <ToggleButton value="ids">By booking IDs</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    )}

                    {effMode === 'ids' ? (
                        <Stack spacing={1.5}>
                            {bookingIds.length > IDS_COLLAPSE_THRESHOLD ? (
                                <Alert
                                    severity="info"
                                    action={<Button color="inherit" size="small" onClick={() => setBookingIds([])}>Clear</Button>}
                                >
                                    {`${bookingIds.length} booking IDs loaded.`}
                                </Alert>
                            ) : (
                                <Autocomplete<string, true, false, true>
                                    multiple
                                    freeSolo
                                    autoSelect
                                    options={[]}
                                    value={bookingIds}
                                    onChange={(_e, v) => setBookingIds(Array.from(new Set(v.map((s) => String(s).trim()).filter(Boolean))))}
                                    renderTags={(value, getTagProps) => value.map((id, index) => (
                                        <Chip size="small" label={id} {...getTagProps({ index })} key={id} />
                                    ))}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Booking IDs"
                                            size="small"
                                            placeholder="Type an ID and press Enter"
                                            helperText="Each ID becomes a badge. Ranges and product filters are not used in this mode."
                                        />
                                    )}
                                />
                            )}
                            <Box>
                                <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />}>
                                    Upload file (one ID per line)
                                    <input hidden type="file" accept=".txt,.csv,text/plain" onChange={handleIdsFile} data-testid="booking-ids-file" />
                                </Button>
                                {fileNote && (
                                    <Typography
                                        variant="caption"
                                        color={fileNote.severity === 'success' ? 'text.secondary' : `${fileNote.severity}.main`}
                                        sx={{ ml: 1.5 }}
                                    >
                                        {fileNote.text}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    ) : (
                        <>
                            <Autocomplete<TargetOption, true, false, false>
                                multiple
                                options={options}
                                value={selected}
                                onChange={(_e, v) => setSelected(v)}
                                disabled={!capsReady || targetsLoading}
                                groupBy={groupOf}
                                getOptionLabel={optionLabel}
                                isOptionEqualToValue={sameOption}
                                filterOptions={(opts, state) => {
                                    const q = state.inputValue.trim().toLowerCase();
                                    if (!q) {
                                        return opts;
                                    }
                                    return opts.filter((o) => o.tourName.toLowerCase().includes(q)
                                        || String(o.tourId) === q
                                        || o.value.toLowerCase().includes(q));
                                }}
                                renderGroup={(params) => {
                                    const groupOptions = options.filter((o) => groupOf(o) === params.group);
                                    const tourId = groupOptions[0]?.tourId;
                                    const inSelection = groupOptions.filter((o) => pickedIds.has(o.id)).length;
                                    return (
                                        <li key={params.key}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    px: 1,
                                                    py: 0.25,
                                                    position: 'sticky',
                                                    top: -8,
                                                    bgcolor: 'background.paper',
                                                    zIndex: 1,
                                                    borderBottom: 1,
                                                    borderColor: 'divider',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => tourId !== undefined && toggleTour(tourId)}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={inSelection > 0 && inSelection === groupOptions.length}
                                                    indeterminate={inSelection > 0 && inSelection < groupOptions.length}
                                                    sx={{ py: 0.5 }}
                                                />
                                                <Typography variant="subtitle2">{params.group}</Typography>
                                            </Box>
                                            <Box component="ul" sx={{ p: 0 }}>
                                                {params.children}
                                            </Box>
                                        </li>
                                    );
                                }}
                                renderOption={(props, o) => (
                                    <Box component="li" {...props} key={o.id} sx={{ pl: 4 }}>
                                        <Checkbox size="small" checked={pickedIds.has(o.id)} sx={{ py: 0, mr: 1 }} />
                                        <code>{o.value}</code>
                                        <Chip size="small" variant="outlined" label={o.kind === 'primary' ? 'primary' : o.idType} sx={{ ml: 1 }} />
                                    </Box>
                                )}
                                renderTags={(vals, getTagProps) => vals.map((o, index) => (
                                    <Chip {...getTagProps({ index })} key={o.id} size="small" label={`${o.value} (${o.tourName})`} />
                                ))}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Tours / external IDs"
                                        placeholder={scopeReady ? 'Search by tour name, tour ID or external ID' : 'Choose a booking system first'}
                                        helperText={scopeReady && targets
                                            ? `${options.length} selectable IDs across ${targets.length} tours — empty selection syncs all products`
                                            : (locked && targetsLoading ? 'Loading the tours…' : undefined)}
                                    />
                                )}
                            />

                            {updGran !== null && !canCombine && (
                                <Box>
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={rangeMode}
                                        onChange={(_e, v) => v && setRangeMode(v as 'start' | 'updated')}
                                    >
                                        <ToggleButton value="start">By start date</ToggleButton>
                                        <ToggleButton value="updated">{`By ${updatedLabel.toLowerCase()} time`}</ToggleButton>
                                    </ToggleButtonGroup>
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                                        {`${integrationLabel} cannot combine both ranges in one query`}
                                    </Typography>
                                </Box>
                            )}

                            {startActive && (
                                /* Top-aligned: helper text under the "To" field must not push the sibling inputs
                                   off the shared baseline. */
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
                                    <RangePicker
                                        granularity={startGran}
                                        label="From"
                                        value={fromDate}
                                        onChange={setFromDate}
                                        width={startGran === 'datetime' ? 230 : 170}
                                    />
                                    <RangePicker
                                        granularity={startGran}
                                        label="To"
                                        value={toDate}
                                        onChange={setToDate}
                                        width={startGran === 'datetime' ? 230 : 170}
                                        helperText={startGran === 'datetime' ? '00:00 → end of day' : undefined}
                                    />
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ alignItems: 'center', minHeight: 40 }}>
                                        {QUICK_RANGES.map((r) => (
                                            <Chip
                                                key={r.label}
                                                size="small"
                                                label={r.label}
                                                variant="outlined"
                                                onClick={() => fromDate && setToDate(r.compute(fromDate))}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {updatedActive && updGran !== null && (
                                /* Two pickers + preset chips (the console's datetime TimeWindowPicker does not
                                   exist here). */
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
                                    <RangePicker
                                        granularity={updGran}
                                        label={`${updatedLabel} from`}
                                        value={updatedFrom}
                                        onChange={setUpdatedFrom}
                                        width={updGran === 'datetime' ? 230 : 170}
                                    />
                                    <RangePicker
                                        granularity={updGran}
                                        label={`${updatedLabel} to`}
                                        value={updatedTo}
                                        onChange={setUpdatedTo}
                                        width={updGran === 'datetime' ? 230 : 170}
                                        helperText="Empty = up to now"
                                    />
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ alignItems: 'center', minHeight: 40 }}>
                                        {UPDATED_PRESETS.map((r) => (
                                            <Chip
                                                key={r.label}
                                                size="small"
                                                label={r.label}
                                                variant="outlined"
                                                onClick={() => {
                                                    const now = moment();
                                                    setUpdatedFrom(now.clone().subtract(r.hours, 'hours'));
                                                    setUpdatedTo(now);
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </>
                    )}

                    <FormControlLabel
                        control={<Switch checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
                        label="Dry run — fetch bookings + build XML artifacts, do not import"
                    />

                    <Accordion disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">Advanced options</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={2}>
                                <Box sx={{ width: 320, maxWidth: '100%' }}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Import source (source_id override)"
                                        value={source}
                                        onChange={(e) => setSource(e.target.value)}
                                        inputProps={{ 'data-testid': 'import-source' }}
                                    >
                                        <MenuItem value="">Default from integration config</MenuItem>
                                        {importSources.map((s) => <MenuItem key={s.code} value={s.code}>{`${s.name} (${s.code})`}</MenuItem>)}
                                    </TextField>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <FormControlLabel
                                        control={<Switch checked={sendNotifications} onChange={(e) => setSendNotifications(e.target.checked)} />}
                                        label="Send notifications"
                                    />
                                    <FormControlLabel
                                        control={<Switch checked={sendSurveys} onChange={(e) => setSendSurveys(e.target.checked)} />}
                                        label="Send surveys"
                                    />
                                    <FormControlLabel
                                        control={<Switch checked={reconcile} onChange={(e) => setReconcile(e.target.checked)} />}
                                        label="Reconcile existing bookings"
                                    />
                                    <FormControlLabel
                                        control={(
                                            <Switch
                                                checked={approval && !dryRun}
                                                disabled={dryRun}
                                                onChange={(e) => setApproval(e.target.checked)}
                                            />
                                        )}
                                        label="Review before import (approval step)"
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Batch multiplier"
                                        value={batchMultiplier}
                                        onChange={(e) => {
                                            const n = Number(e.target.value);
                                            setBatchMultiplier(Number.isFinite(n) ? Math.min(8, Math.max(1, Math.trunc(n))) : 1);
                                        }}
                                        helperText="Parallel import batches (1 = sequential)"
                                        inputProps={{ min: 1, max: 8 }}
                                        sx={{ width: 180 }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Booking statuses"
                                        placeholder="e.g. CONFIRMED, CANCELLED (comma-separated; empty = integration default)"
                                        value={statuses}
                                        onChange={(e) => setStatuses(e.target.value)}
                                        sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 320 } }}
                                    />
                                </Box>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {startError != null && (
                        <Alert severity="warning" data-testid="booking-sync-error">{refusalText(startError)}</Alert>
                    )}

                    <Box>
                        <Button
                            variant="contained"
                            size="large"
                            color={dryRun ? 'secondary' : 'primary'}
                            disabled={!capsReady || !rangesOk || missingRequiredArgs.length > 0 || starting}
                            onClick={start}
                        >
                            {dryRun ? 'Start dry run' : 'Start booking sync'}
                        </Button>
                        {missingRequiredArgs.length > 0 && (
                            <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>
                                {`${missingRequiredArgs.map((a) => a.label).join(', ')} required`}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </Box>
        </LocalizationProvider>
    );
}
